import { useEffect, useState } from "react";
import CreateMenu from "@/components/CreateMenu";
import ComposeModal from "@/components/feed/ComposeModal";
import UploadModal from "@/components/upload-modal";
import { readFixtureNameFromUrl } from "@/lib/fixture-loader";

/**
 * Owns the global Create entry flow (picker + downstream Compose / Upload modals)
 * so the listeners are mounted anywhere the authenticated app shell is rendered,
 * not only on /home. Dispatched window events: openCreate, openCompose,
 * openComposeWithDrafts, openUpload, openUploadEpisode.
 */
export default function GlobalCreateFlow() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [composeShowDrafts, setComposeShowDrafts] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadInitialTab, setUploadInitialTab] = useState<"video" | "episode">("video");
  const [fixtureName, setFixtureName] = useState<string | null>(null);

  useEffect(() => {
    const handleOpenCreate = () => setIsCreateOpen(true);
    const handleOpenCompose = () => setIsComposeOpen(true);
    const handleOpenComposeWithDrafts = () => { setComposeShowDrafts(true); setIsComposeOpen(true); };
    const handleOpenUpload = () => { setUploadInitialTab("video"); setIsUploadOpen(true); };
    const handleOpenUploadEpisode = () => { setUploadInitialTab("episode"); setIsUploadOpen(true); };

    window.addEventListener("openCreate", handleOpenCreate);
    window.addEventListener("openCompose", handleOpenCompose);
    window.addEventListener("openComposeWithDrafts", handleOpenComposeWithDrafts);
    window.addEventListener("openUpload", handleOpenUpload);
    window.addEventListener("openUploadEpisode", handleOpenUploadEpisode);
    return () => {
      window.removeEventListener("openCreate", handleOpenCreate);
      window.removeEventListener("openCompose", handleOpenCompose);
      window.removeEventListener("openComposeWithDrafts", handleOpenComposeWithDrafts);
      window.removeEventListener("openUpload", handleOpenUpload);
      window.removeEventListener("openUploadEpisode", handleOpenUploadEpisode);
    };
  }, []);

  // Dev-only: ?timeline-fixture=<name> auto-opens the upload modal with the
  // named fixture project. Used by the Step 9 preview-capture harness. The
  // readFixtureNameFromUrl helper returns null in production so this effect
  // is a no-op there.
  useEffect(() => {
    const name = readFixtureNameFromUrl();
    if (!name) return;
    setFixtureName(name);
    setUploadInitialTab("video");
    setIsUploadOpen(true);
  }, []);

  return (
    <>
      <CreateMenu isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
      <ComposeModal
        isOpen={isComposeOpen}
        onClose={() => { setIsComposeOpen(false); setComposeShowDrafts(false); }}
        initialShowDrafts={composeShowDrafts}
      />
      <UploadModal
        isOpen={isUploadOpen}
        onClose={() => { setIsUploadOpen(false); setFixtureName(null); }}
        initialTab={uploadInitialTab}
        fixtureName={fixtureName}
      />
    </>
  );
}
